import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Icon } from "../../ui/UIElements/Icon";
import UserCard from "../../ui/Users/UserCard";
import Pagination from "../../ui/Data/Pagination";
import UsersLoading from "../../ui/Users/UsersLoading";
import UsersError from "../../ui/Users/UsersError";
import { StatsGrid } from "../../ui/Data/StatCard";
import { useUsersData } from "../../../hooks/useUsers";
import { useExportUsers } from "../../../hooks/useExportUsers";
import { useUrlStates, usePostHog } from "@finitless/shared/hooks";
import { toast } from "sonner";
import {
  ITEMS_PER_PAGE,
  MAX_VISIBLE_PAGES,
  USER_STATUS_FILTER_TABS,
} from "../../../constants/userConstants";
import { PageHeader } from "@finitless/shared/components";

const CustomersPage = () => {
  const { t } = useTranslation("customers");
  const navigate = useNavigate();

  const [urlStates, updateUrlState] = useUrlStates({
    enabledFilter: "all",
    searchTerm: "",
    currentPage: "1",
  });

  const [searchInput, setSearchInput] = useState(urlStates.searchTerm || "");
  const [enabledFilter, setEnabledFilter] = useState(
    urlStates.enabledFilter || "all",
  );
  const [searchTerm, setSearchTerm] = useState(urlStates.searchTerm || "");
  const [currentPage, setCurrentPage] = useState(
    parseInt(urlStates.currentPage) || 1,
  );

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchInput]);

  // Get customer data only
  const { stats, users, pagination, isLoading, error, filterTabCounts } =
    useUsersData("customers", enabledFilter, searchTerm, currentPage);

  // Export users hook
  const exportUsersMutation = useExportUsers();
  const { trackFeatureAdoption } = usePostHog();

  const paginationData = pagination;

  // Calculate totalPages manually if not provided by API
  const calculatedTotalPages = paginationData?.total
    ? Math.ceil(paginationData.total / ITEMS_PER_PAGE)
    : 1;

  const totalPages = paginationData?.totalPages || calculatedTotalPages;

  const getFilterLabel = (filterKey) => {
    const labelByFilter = {
      enabled: t("filters.active"),
      disabled: t("filters.inactive"),
      all: t("filters.allCustomers"),
    };
    return labelByFilter[filterKey] || filterKey;
  };

  const handleEnabledFilterChange = (newEnabledFilter) => {
    setEnabledFilter(newEnabledFilter);
    setCurrentPage(1);
    updateUrlState("enabledFilter", newEnabledFilter);
    updateUrlState("currentPage", "1");
  };

  const handleSearchChange = (term) => {
    setSearchInput(term);
    setCurrentPage(1);
    updateUrlState("searchTerm", term);
    updateUrlState("currentPage", "1");
  };

  const handleClearSearch = () => {
    setSearchInput("");
    setSearchTerm("");
    setCurrentPage(1);
    updateUrlState("searchTerm", "");
    updateUrlState("currentPage", "1");
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    updateUrlState("currentPage", page.toString());
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleViewDetails = (user) => {
    navigate(`/users/${user.id}`, { state: { user } });
  };

  const handleExportUsers = async () => {
    try {
      await exportUsersMutation.mutateAsync({
        userType: "customers",
        enabledFilter,
        searchTerm,
      });

      trackFeatureAdoption.dataExported({
        export_type: "users",
        format: "csv",
        user_type: "customers",
        enabled_filter: enabledFilter,
      });

      toast.success(t("toasts.exportSuccessTitle"), {
        description: t("toasts.exportSuccessDescription"),
        duration: 5000,
      });
    } catch {
      toast.error(t("toasts.exportFailedTitle"), {
        description: t("toasts.exportFailedDescription"),
        duration: 5000,
      });
    }
  };

  const metricCards = [
    {
      id: "total-customers",
      title: t("stats.totalCustomers"),
      value: stats?.totalcustomers || stats?.totalCustomers || 0,
      icon: "Users",
      iconColor: "text-green-400",
      iconBg: "bg-green-500/10",
      gradient: true,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col justify-between sm:flex-row sm:items-center">
        <PageHeader
          title={t("header.title")}
          subtitle={t("subtitle.customers")}
        />
        <div className="flex space-x-3">
          <button
            onClick={handleExportUsers}
            disabled={exportUsersMutation.isPending}
            className="flex items-center space-x-2 rounded-lg bg-gray-800 px-3 py-2 text-sm text-white transition-colors hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-50 sm:px-4"
          >
            <Icon name="Download" className="h-4 w-4" />
            <span className="hidden sm:inline">
              {exportUsersMutation.isPending
                ? t("actions.exporting")
                : t("actions.exportCustomers")}
            </span>
            <span className="sm:hidden">
              {exportUsersMutation.isPending ? "..." : t("actions.export")}
            </span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <StatsGrid
        stats={metricCards}
        columns={metricCards.length}
        className="mb-4 sm:mb-6"
      />

      {/* Status Tabs */}
      <div className="border-b border-gray-800">
        <nav className="flex space-x-4 overflow-x-auto sm:space-x-8">
          {USER_STATUS_FILTER_TABS.map(({ key, color }) => {
            const count = filterTabCounts[key] || 0;

            return (
              <button
                key={key}
                onClick={() => handleEnabledFilterChange(key)}
                className={`border-b-2 px-1 py-3 text-xs font-medium whitespace-nowrap transition-colors sm:py-4 sm:text-sm ${
                  enabledFilter === key
                    ? `border-current ${color}`
                    : "border-transparent text-gray-400 hover:border-gray-300 hover:text-gray-300"
                }`}
              >
                {getFilterLabel(key)} ({count})
              </button>
            );
          })}
        </nav>
      </div>

      {/* Search */}
      <div className="mb-6 rounded-lg border border-gray-800 bg-gray-900 p-4">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-1 flex-col gap-4 sm:flex-row">
            {/* Search */}
            <div className="min-w-0 flex-1">
              <div className="relative">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <Icon name="Search" className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder={t("search.placeholder", {
                    entity: t("tabs.customers").toLowerCase(),
                  })}
                  value={searchInput}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="h-10 w-full rounded-lg border border-gray-700 bg-gray-800 px-4 py-2 pr-10 pl-10 text-white placeholder-gray-400 focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
                {searchInput && (
                  <button
                    onClick={handleClearSearch}
                    className="absolute top-1/2 right-2 flex h-5 w-5 -translate-y-1/2 transform cursor-pointer items-center justify-center rounded-full bg-gray-600 transition-colors hover:bg-gray-500"
                    type="button"
                  >
                    <svg
                      className="h-3 w-3 text-gray-300 hover:text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {isLoading ? (
        <UsersLoading />
      ) : error ? (
        <UsersError error={error} />
      ) : users.length > 0 ? (
        <>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {users.map((user) => (
              <UserCard
                key={user.id}
                user={user}
                onViewDetails={handleViewDetails}
              />
            ))}
          </div>

          {/* Pagination */}
          <div className="sticky bottom-0 z-10 mt-8 rounded-lg border border-gray-800 bg-gray-900 p-4 shadow-[0_-4px_12px_rgba(0,0,0,0.3)]">
            <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
              <div className="text-sm text-gray-400">
                {t("pagination.showing", {
                  from: (currentPage - 1) * ITEMS_PER_PAGE + 1,
                  to: Math.min(
                    currentPage * ITEMS_PER_PAGE,
                    paginationData?.total || 0,
                  ),
                  total: paginationData?.total || 0,
                })}
              </div>

              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                maxVisiblePages={MAX_VISIBLE_PAGES}
                disabled={isLoading}
                className="mt-4 sm:mt-0"
              />
            </div>
          </div>
        </>
      ) : (
        <div className="py-12 text-center">
          <Icon name="Users" className="mx-auto mb-4 h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-300">
            {t("empty.title")}
          </h3>
          <p className="mt-1 text-sm text-gray-400">
            {searchTerm
              ? t("empty.subtitleSearch")
              : t("empty.subtitleDefault", { entity: t("tabs.customers") })}
          </p>
        </div>
      )}
    </div>
  );
};

export default CustomersPage;
